import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormGroup } from 'reactstrap';
import FormInput, { defaultValue, valueKey } from '../components/portal/elements/FormInput';
import { useApi } from '../components/portal/elements/ApiState';
import { usePageState } from '../components/PageState';

const FormContext = React.createContext(null);

function getChangeValue(setState) {
	return name => {
		return e => {
			setState(state => {
				let value;
				if(typeof e === 'function') {
					value = e(state?.values?.[name]);
				} else {
					value = e.target.value;
				}
				let newState = state;
				if(!state.values) {
					newState = { ...state, values: { [name]: value } };
				} else if(state.values[name] !== value || !(name in state.values)) {
					newState = { ...state, values: { ...state.values, [name]: value } };
				}
				if(newState !== state) {
					newState.context = { ...state.context, values: newState.values };
				}
				return newState;
			});
		};
	};
}

function useValues() {
	const [state, setState] = useState({
		values: null,
		context: {
			values: null,
			changeValue: null,
			submitting: null
		}
	});
	const { changeValue, setSubmitting, updateValues } = useMemo(() => {
		return {
			changeValue: getChangeValue(setState),
			setSubmitting(value) {
				setState(prev => {
					if(typeof value === 'function') {
						value = value(prev.context.submitting);
					}
					if(prev.context.submitting === value) {
						return prev;
					}
					return { ...prev, context: { ...prev.context, submitting: value } };
				});
			},
			updateValues(values) {
				setState(prev => {
					if(typeof values === 'function') {
						values = values(prev.values);
					}
					if(prev.values === values) {
						return prev;
					}
					return { ...prev, values, context: { ...prev.context, values } };
				});
			}
		};
	}, [setState]);
	state.context.changeValue = changeValue;
	return { state, setState, updateValues, setSubmitting };
}

function useAutosubmit(onChange, values, forceFormData) {
	const { api, submitForm } = useApi();
	const [prevValues, setPrevValues] = useState(values);
	useEffect(() => {
		if(api && onChange) {
			if(typeof onChange === 'object') {
				const { url, names } = onChange;
				const prev = prevValues || {};
				const vals = values || {};
				for(const key of names) {
					if(prev[key] !== vals[key]) {
						submitForm(api, url, values, forceFormData);
						break;
					}
				}
			} else {
				submitForm(api, onChange, values, forceFormData);
			}
		}
		setPrevValues(values);
	}, [values]);
}

function deepEquals(obj1, obj2) {
	if(obj1 === obj2) {
		return true;
	} else if(typeof obj1 === 'object' && typeof obj2 === 'object') {
		if(!obj1 || !obj2) {
			return false;
		}
		const array = Array.isArray(obj1);
		if(Array.isArray(obj2)) {
			if(!array) {
				return false;
			}
			if(obj1.length !== obj2.length) {
				return false;
			}
			for(let i = 0; i < obj1.length; i++) {
				if(!deepEquals(obj1[i], obj2[i])) {
					return false;
				}
			}
			return true;
		} else if(array) {
			return false;
		}
		const k1 = Object.keys(obj1);
		const k2 = Object.keys(obj2);
		if(k1.length !== k2.length) {
			return false;
		}
		for(const key in obj1) {
			if(!(key in obj2) || !deepEquals(obj1[key], obj2[key])) {
				return false;
			}
		}
		return true;
	}
	return false;
}

function notEmpty(value) {
	if(!value) {
		return false;
	} else if(Array.isArray(value)) {
		return value.length;
	} else if(value instanceof FileList) {
		return value.length;
	}
	return true;
}

function ruleMatches(rule, values) {
	if(rule.notEmpty) {
		const value = values[rule.notEmpty];
		return notEmpty(value);
	} else if(rule.empty) {
		const value = values[rule.empty];
		return !notEmpty(value);
	} else if(rule.equals) {
		const v = values[rule.equals[0]];
		return rule.equals.every(value => deepEquals(values[value], v));
	} else if(rule.notIn) {
		return rule.notIn.values.every(value => !deepEquals(values[rule.notIn.name], value));
	} else if(rule.in) {
		return rule.in.values.some(value => deepEquals(values[rule.in.name], value));
	} else if(rule.or) {
		return rule.or.some(r => ruleMatches(r, values));
	} else if(rule.and) {
		return rule.and.every(r => ruleMatches(r, values));
	}
	return false;
}

export function checkRequired(require, values) {
	if(Array.isArray(require)) {
		return values !== null && require.every(rule => ruleMatches(rule, values));
	}
	return true;
}

export function FormProvider({ context, children }) {
	return <FormContext.Provider value={context} children={children} />;
}

export function useFormContext() {
	return useContext(FormContext);
}

export default Child => {
	return ({ inputs, onChange, ...props }) => {
		const { values: pageValues, actions } = usePageState();
		const setValues = props.id && pageValues.find(f => f.form === props.id)?.values;
		const { state, updateValues, setSubmitting } = useValues();
		useEffect(() => {
			const values = {};
			inputs?.forEach(input => {
				defaultValue(input, v => {
					values[input.name] = v;
				});
			});
			updateValues(prev => ({ ...prev, ...values }));
		}, [inputs]);
		useEffect(() => {
			if(setValues) {
				updateValues(prev => ({ ...prev, ...setValues }));
				actions.clearFormValue(props.id);
			}
		}, [setValues, actions.clearFormValue]);
		const { values, context } = state;
		useAutosubmit(onChange, values, props.forceFormData);
		const renderInputs = useMemo(() => {
			return () => {
				return inputs && values && inputs.map((input, i) => {
					const { key, ...pass } = input;
					pass[valueKey(input)] = values[input.name];
					pass.onChange = context.changeValue(input.name);
					return <FormGroup key={key || i}>
						<FormInput {...pass} />
					</FormGroup>;
				});
			};
		}, [inputs, values]);
		const canSubmit = useMemo(() => require => checkRequired(require, values));
		if(inputs && !values) {
			return null;
		}
		return <FormProvider context={context}>
			<Child {...props} renderInputs={renderInputs} values={values} canSubmit={canSubmit} setSubmitting={setSubmitting} submitting={context.submitting} />
		</FormProvider>;
	};
};
